// api/onboardingProgresoApi.js
// Funciones para interactuar con la API de persistencia de progreso del onboarding

import axios from "axios"

/**
 * Guarda el progreso actual del onboarding
 *
 * @param {Object} datos - Datos a guardar
 * @param {string} datos.cuit - CUIT del titular
 * @param {number} datos.pasoActual - Índice del paso actual (0-indexed)
 * @param {string} datos.nombrePaso - Nombre del paso actual
 * @param {Object} datos.datosFormulario - Valores del formulario (formik values)
 * @param {Object} datos.datosAdicionales - Datos adicionales (calificación, evaluación, etc.)
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const guardarProgresoOnboarding = async datos => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/progreso`,
      datos,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

/**
 * Recupera el progreso guardado para un CUIT
 *
 * @param {string} cuit - CUIT del titular
 * @returns {Promise<Object>} Progreso guardado o { tieneProgresoGuardado: false }
 */
export const recuperarProgresoOnboarding = async cuit => {
  try {
    if (!cuit) {
      return { tieneProgresoGuardado: false }
    }
    const cuitLimpio = cuit.replace(/[^0-9]/g, "")
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/progreso/${cuitLimpio}`,
    )
    return response.data
  } catch (error) {
    // throw new Error(error.response?.data?.message || error.message)
    console.error("Error recuperando progreso:", error)
    return { tieneProgresoGuardado: false }
  }
}

/**
 * Verifica si existe un progreso activo para un CUIT
 *
 * @param {string} cuit - CUIT del titular
 * @returns {Promise<boolean>} true si existe progreso activo
 */
export const existeProgresoOnboarding = async cuit => {
  try {
    if (!cuit) {
      return false
    }
    const cuitLimpio = cuit.replace(/[^0-9]/g, "")
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/progreso/${cuitLimpio}/existe`,
    )
    return response.data
  } catch (error) {
    // throw new Error(error.response?.data?.message || error.message)
    console.error("Error verificando progreso:", error)
    return false
  }
}

/**
 * Marca el progreso como completado
 *
 * @param {string} cuit - CUIT del titular
 */
export const marcarProgresoCompletado = async cuit => {
  try {
    if (!cuit) return
    const cuitLimpio = cuit.replace(/[^0-9]/g, "")
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/progreso/${cuitLimpio}/completar`,
      {},
    )
    return response.data
  } catch (error) {
    // throw new Error(error.response?.data?.message || error.message)
    console.error("Error marcando progreso como completado:", error)
  }
}

/**
 * Marca el progreso como abandonado
 *
 * @param {string} cuit - CUIT del titular
 */
export const marcarProgresoAbandonado = async cuit => {
  try {
    if (!cuit) return
    const cuitLimpio = cuit.replace(/[^0-9]/g, "")
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/progreso/${cuitLimpio}/abandonar`,
      {},
    )
    return response.data
  } catch (error) {
    // throw new Error(error.response?.data?.message || error.message)
    console.error("Error marcando progreso como abandonado:", error)
  }
}

/**
 * Descarta el progreso actual (para empezar de nuevo)
 *
 * @param {string} cuit - CUIT del titular
 */
export const descartarProgresoOnboarding = async cuit => {
  try {
    if (!cuit) return
    const cuitLimpio = cuit.replace(/[^0-9]/g, "")
    const response = await axios.delete(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/progreso/${cuitLimpio}`,
    )
    return response.data
  } catch (error) {
    // throw new Error(error.response?.data?.message || error.message)
    console.error("Error descartando progreso:", error)
  }
}

/**
 * Obtiene estadísticas del funnel de onboarding
 *
 * @returns {Promise<Object>} Estadísticas agregadas
 */
export const obtenerEstadisticasOnboarding = async () => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/estadisticas`,
    )
    return response.data
  } catch (error) {
    console.error("Error obteniendo estadísticas:", error)
    throw new Error(error.response?.data?.message || error.message)
  }
}

/**
 * Obtiene todos los progresos pendientes (EN_PROGRESO)
 * @returns {Promise<Array>} Lista de progresos pendientes
 */
export const obtenerProgresosPendientes = async () => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/pendientes`,
    )
    return response.data || []
  } catch (error) {
    console.error("Error obteniendo pendientes:", error)
    return []
  }
}
